/**
 * Platform admin — SAM Onboarding Prompt Editor
 *
 * Displays and edits the `onboarding_agent_prompts` configuration catalogue:
 *  • General section  — shared_discipline (cross-page behavioural rules)
 *  • Per-page section — one card per wizard step, each with its own body prompt
 *
 * All values are stored as plain strings in the system catalogue layer.
 * An empty string means "use the built-in default" — saving empty resets to default.
 * Uses the existing generic configuration endpoints via platformAdminApi hooks.
 */
import React, { useEffect, useState } from 'react'
import PageShell from '@/components/Layout/PageShell'
import { Modal } from '@/components/UI/Modal'
import { PageHeader } from '@/components/UI/PageHeader'
import { Badge } from '@/components/UI/Badge'
import { Button } from '@/components/UI/Button'
import { Alert } from '@/components/UI/Alert'
import { EmptyState } from '@/components/UI/EmptyState'
import { LoadingState } from '@/components/UI/LoadingState'
import { fieldClass } from '@/components/UI/fieldStyles'
import { cn } from '@/utils/cn'
import {
  useListCatalogueEntriesQuery,
  useUpdateCatalogueConfigMutation,
  useGetPromptVersionsQuery,
  useListAgentPromptsQuery,
  type PromptVersion,
} from '@/redux/internal-services/platformAdminApi'

// Turn a setting key into a human title: "page_company_info" → "Company Info",
// "shared_discipline" → "Shared Discipline", "role_intro" → "Role Intro". Works for any agent.
function humanizeKey(key: string): string {
  return key
    .replace(/^page_/, '')
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// Derive the {variable} chips for a setting by scanning its text for {token}s — agent-agnostic.
function deriveVars(text: string): string[] {
  const found = new Set<string>()
  for (const m of (text || '').matchAll(/\{[a-z0-9_]+\}/gi)) found.add(m[0])
  return [...found]
}

// ── History item naming (persisted in localStorage) ──────────────────────────
// Names are stored locally keyed by setting + saved_at, so each saved version keeps the title the
// user gave it across reloads. (The version records themselves live on the server.)

const NAMES_LS_KEY = 'sam_prompt_version_names'

function loadVersionNames(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(NAMES_LS_KEY) || '{}') } catch { return {} }
}
function persistVersionNames(map: Record<string, string>) {
  try { localStorage.setItem(NAMES_LS_KEY, JSON.stringify(map)) } catch { /* storage full / disabled */ }
}
function versionKey(v: PromptVersion) { return `${v.setting}|${v.saved_at}` }

// Auto-generated default title: first non-empty line of the prompt (≤40 chars), else "Untitled #N".
function defaultVersionName(v: PromptVersion, index: number): string {
  const firstLine = (v.value || '').split('\n').map(s => s.trim()).find(Boolean)
  if (firstLine) return firstLine.length > 40 ? firstLine.slice(0, 40) + '…' : firstLine
  return `Untitled prompt #${index + 1}`
}

// ── Read-only "View" modal for a single history item ──────────────────────────

const VersionViewModal: React.FC<{
  version: PromptVersion
  title: string
  settingLabel: string
  onClose: () => void
}> = ({ version, title, settingLabel, onClose }) => (
  <Modal title={title} onClose={onClose} maxWidthClassName="max-w-2xl">
    <p className="text-xs text-ui-text3">
      {settingLabel} · {new Date(version.saved_at).toLocaleString()}
      {version.saved_by ? ` · saved by ${version.saved_by.slice(0, 8)}…` : ''}
    </p>
    <div>
      <p className="eyebrow mb-1">Prompt text</p>
      {version.value
        ? <pre className="whitespace-pre-wrap rounded-control border border-ui-border bg-ui-subtle p-3 font-mono text-xs text-ui-text">{version.value}</pre>
        : <p className="text-xs text-ui-text3 italic">Empty — this version used the built-in default.</p>}
    </div>
    <p className="text-xs text-ui-text3">
      The cross-page system prompt (Shared Discipline) and any model response aren't captured per
      saved version — only the prompt text above is stored.
    </p>
  </Modal>
)

// ── Version history panel ───────────────────────────────────────────────────

const VersionHistory: React.FC<{
  settingKey: string
  settingLabel: string
  catalogueKey: string
  onRestore: (value: string) => void
}> = ({ settingKey, settingLabel, catalogueKey, onRestore }) => {
  const { data, isLoading } = useGetPromptVersionsQuery({ setting: settingKey, key: catalogueKey, limit: 10 })
  const versions: PromptVersion[] = data?.data ?? []

  const [names, setNames] = useState<Record<string, string>>(() => loadVersionNames())
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [viewing, setViewing] = useState<PromptVersion | null>(null)

  const commitRename = (k: string) => {
    const next = { ...names }
    const trimmed = editValue.trim()
    if (trimmed) next[k] = trimmed
    else delete next[k] // empty → fall back to the auto-generated default
    setNames(next)
    persistVersionNames(next)
    setEditingKey(null)
  }

  if (isLoading) return <LoadingState label="Loading history…" className="py-4" />
  if (!versions.length) return <EmptyState variant="inline" title="No versions saved yet." />

  return (
    <>
      <ol className="max-h-64 space-y-2 overflow-y-auto">
        {versions.map((v, i) => {
          const k = versionKey(v)
          const isSeed = v.saved_by === 'system:seed'
          const name = isSeed ? 'System default (seed)' : (names[k] ?? defaultVersionName(v, i))
          const isEditing = !isSeed && editingKey === k
          return (
            <li key={k} className="group flex items-start gap-2 rounded-control border border-ui-border bg-ui-subtle p-2 text-xs">
              <div className="min-w-0 flex-1">
                {/* Editable name */}
                {isEditing ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => commitRename(k)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(k) }
                      if (e.key === 'Escape') { setEditingKey(null) }
                    }}
                    className={cn(fieldClass({ size: 'xs' }), 'font-medium')}
                  />
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-ui-text">{name}</span>
                    {isSeed ? (
                      <Badge variant="accent" className="shrink-0">SEED</Badge>
                    ) : (
                      <button
                        onClick={() => { setEditingKey(k); setEditValue(names[k] ?? name) }}
                        title="Rename"
                        aria-label="Rename this version"
                        className="shrink-0 text-ui-text3 opacity-0 transition-opacity hover:text-ui-text focus:opacity-100 group-hover:opacity-100"
                      >
                        ✎
                      </button>
                    )}
                  </div>
                )}
                <span className="mt-0.5 block text-ui-text3">
                  {isSeed
                    ? `System baseline · ${new Date(v.saved_at).toLocaleString()}`
                    : `${new Date(v.saved_at).toLocaleString()}${v.saved_by ? ` · by ${v.saved_by.slice(0, 8)}…` : ''}`}
                </span>
                <p className="mt-0.5 truncate font-mono text-ui-text2">
                  {v.value ? v.value.slice(0, 100) + (v.value.length > 100 ? '…' : '') : <em className="text-ui-text3">empty (built-in default)</em>}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Button variant="ghost" size="xs" onClick={() => setViewing(v)} title="View full prompt">
                  👁 View
                </Button>
                <Button variant="soft" size="xs" onClick={() => onRestore(v.value)}>
                  Restore
                </Button>
              </div>
            </li>
          )
        })}
      </ol>

      {viewing && (
        <VersionViewModal
          version={viewing}
          title={viewing.saved_by === 'system:seed' ? 'System default (seed)' : (names[versionKey(viewing)] ?? defaultVersionName(viewing, versions.indexOf(viewing)))}
          settingLabel={settingLabel}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  )
}

// ── Single prompt card ──────────────────────────────────────────────────────

interface PromptCardProps {
  title: string
  subtitle?: string
  settingKey: string
  currentValue: string
  saving: boolean
  onSave: (key: string, value: string) => Promise<void>
  placeholder?: string
  vars?: string[]
  /** Catalogue key this setting belongs to (for the version-history query). */
  catalogueKey: string
  /**
   * Seeded, application-level baseline for this prompt (from the catalogue's settings.default).
   * When provided, the textarea is pre-filled with it on first load (if there's no saved override)
   * and a "Reset to default" button repopulates the textarea with it, discarding edits.
   */
  seededDefault?: string
}

const PromptCard: React.FC<PromptCardProps> = ({
  title, subtitle, settingKey, currentValue, saving, onSave, placeholder, vars, catalogueKey, seededDefault,
}) => {
  // What the field shows when there's no unsaved edit: the saved override, or — if none — the
  // seeded default (so the authoritative baseline is visible on first load).
  const baseline = currentValue !== '' ? currentValue : (seededDefault ?? '')
  const [draft, setDraft] = useState(baseline)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [showHistory, setShowHistory] = useState(false)

  // Keep draft in sync when the catalogue data re-loads (e.g. after a save by another tab).
  React.useEffect(() => { setDraft(baseline) }, [baseline])

  const handleSave = async () => {
    setStatus('saving')
    try {
      await onSave(settingKey, draft)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }

  const handleReset = async () => {
    setDraft('')
    setStatus('saving')
    try {
      await onSave(settingKey, '')
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }

  const isDirty = draft !== baseline
  const isSaving = saving || status === 'saving'

  return (
    <div className="card-surface flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-ui-text">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-ui-text3">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="xs" onClick={() => setShowHistory(h => !h)}>
            {showHistory ? 'Hide history' : 'History'}
          </Button>
          {seededDefault !== undefined ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setDraft(seededDefault)}
              disabled={isSaving || draft === seededDefault}
              title="Reset the editor to the seeded default value, discarding your edits"
            >
              Reset to default
            </Button>
          ) : currentValue && (
            <Button
              variant="destructive-ghost"
              size="xs"
              onClick={handleReset}
              disabled={isSaving}
              title="Reset to built-in default (clear override)"
            >
              Reset
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={!isDirty || isSaving}>
            {status === 'saving' ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder ?? 'Leave empty to use the built-in default.'}
        rows={8}
        spellCheck={false}
        className={cn(fieldClass({ shape: 'box' }), 'resize-y font-mono text-xs leading-relaxed md:text-xs')}
      />

      {/* Variable hints (derived from the prompt text) */}
      {vars && vars.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="eyebrow">Variables:</span>
          {vars.map(v => (
            <button
              key={v}
              type="button"
              title="Click to copy · substituted at session start"
              onClick={() => navigator.clipboard?.writeText(v)}
              className="inline-flex cursor-copy items-center rounded-chip border border-accent/20 bg-accent/10 px-1.5 py-0.5 font-mono text-xs text-accent-text transition-colors hover:bg-accent/15 focus-ring"
            >
              {v}
            </button>
          ))}
          <span className="text-xs text-ui-text3">· click to copy · substituted at session start</span>
        </div>
      )}

      {status === 'saved' && (
        <Alert tone="success">Saved. Takes effect on the next voice session.</Alert>
      )}
      {status === 'error' && (
        <Alert tone="danger">Save failed — please try again.</Alert>
      )}

      {showHistory && (
        <div className="border-t border-ui-border pt-3">
          <p className="mb-2 text-xs font-medium text-ui-text2">Version history</p>
          <VersionHistory settingKey={settingKey} settingLabel={title} catalogueKey={catalogueKey} onRestore={(v) => { setDraft(v); setShowHistory(false) }} />
        </div>
      )}
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

const SamPromptsPage: React.FC = () => {
  // Which agents expose editable prompts (dropdown). Defaults to the first one.
  const { data: agentsResp, isLoading: agentsLoading } = useListAgentPromptsQuery()
  const agents = agentsResp?.data ?? []
  const [agentType, setAgentType] = useState<string>('')
  useEffect(() => {
    if (!agentType && agents.length) setAgentType(agents[0].agent_type)
  }, [agents, agentType])
  const selectedAgent = agents.find((a) => a.agent_type === agentType) ?? agents[0]
  const catalogueKey = selectedAgent?.prompt_catalogue_key ?? ''

  const { data, isLoading, isError, refetch } = useListCatalogueEntriesQuery(
    { prefix: catalogueKey },
    { skip: !catalogueKey, refetchOnMountOrArgChange: true },
  )
  const [updateConfig] = useUpdateCatalogueConfigMutation()

  const entry = data?.data?.find((e: { key: string }) => e.key === catalogueKey)
  const effectiveValue: Record<string, string> = (entry?.effective_value as Record<string, string>) ?? {}

  // Seeded defaults + descriptions come from the catalogue schema (settings.*), so the editor is
  // fully data-driven — it renders a card per setting for ANY agent, no hardcoded page list.
  const settings = (entry as { settings?: Record<string, { default?: unknown; description?: string }> } | undefined)?.settings ?? {}
  const settingKeys = Object.keys(settings)
  const generalKeys = settingKeys.filter((k) => !k.startsWith('page_'))
  const topicKeys = settingKeys.filter((k) => k.startsWith('page_'))

  const handleSave = async (settingKey: string, value: string) => {
    await updateConfig({ key: catalogueKey, value: { [settingKey]: value } }).unwrap()
    refetch()
  }

  const renderCard = (k: string) => {
    const s = settings[k] ?? {}
    const seeded = typeof s.default === 'string' ? s.default : ''
    const current = effectiveValue[k] ?? ''
    return (
      <PromptCard
        key={k}
        title={humanizeKey(k)}
        subtitle={s.description}
        settingKey={k}
        currentValue={current}
        seededDefault={seeded}
        vars={deriveVars(current || seeded)}
        catalogueKey={catalogueKey}
        saving={false}
        onSave={handleSave}
      />
    )
  }

  if (agentsLoading || (catalogueKey && isLoading)) {
    return <LoadingState label="Loading prompt settings…" className="h-48" />
  }

  if (!agents.length) {
    return <EmptyState title="No agents with editable prompts are registered." className="h-48" />
  }

  if (isError) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-danger-text">
        Failed to load prompt settings. Refresh to retry.
      </div>
    )
  }

  return (
    <PageShell
      maxWidth="max-w-3xl"
      header={
        <PageHeader
          title="Agent Prompt Editor"
          subtitle="Customise the instructions an agent uses. Changes take effect on the next session — no deployment required. Leave a field empty to use the built-in default."
          toolbar={
            <div className="flex items-center gap-2">
              <label htmlFor="sam-prompts-agent" className="text-xs font-medium text-ui-text2">Agent:</label>
              <select
                id="sam-prompts-agent"
                value={agentType}
                onChange={(e) => setAgentType(e.target.value)}
                className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
              >
                {agents.map((a) => (
                  <option key={a.agent_type} value={a.agent_type}>{a.label}</option>
                ))}
              </select>
            </div>
          }
        />
      }
    >

      {/* General — agent-wide settings (shared discipline, role intro, live-values header, …) */}
      {generalKeys.length > 0 && (
        <section className="space-y-3">
          <h2 className="eyebrow">General — Agent-Wide</h2>
          <div className="space-y-4">{generalKeys.map(renderCard)}</div>
        </section>
      )}

      {/* Per-topic prompts (page_*) */}
      {topicKeys.length > 0 && (
        <section className="space-y-3">
          <h2 className="eyebrow">Per-Topic Instructions</h2>
          <p className="text-xs text-ui-text3">
            Each card is one topic's prompt body. The general settings above and the live field
            values are added automatically.
          </p>
          <div className="space-y-4">{topicKeys.map(renderCard)}</div>
        </section>
      )}

    </PageShell>
  )
}

export default SamPromptsPage
